import { Sentry } from "@/utils/sentryUtils";

export type SerialDeviceConfig<T> = {
  vendorId: number;
  productId: number;
  ctsControl?: boolean;
  bitrate?: number;
  stopBits?: 1 | 2;
  retries?: number;
  timeToWait?: number;
  packetHandler?: (buffer: BufferRef) => T | null;
  statusHandler?: (status: SerialDeviceConnectionStatus) => void;
};

export type BufferRef = {
  get: (n: number) => number;
  len: () => number;
  discard: (n: number) => number[];
  bytes: () => Uint8Array;
  contains: (n: number) => boolean;
};

type ConnectionOpts = {
  baudRate: number;
  autoOpen: boolean;
  bufferSize: number;
  stopBits: 1 | 2 | undefined;
};

export const withDefault = <T>(value: T | undefined, defaultValue: T): T => (value == undefined ? defaultValue : value);

export type SerialDeviceConnectionStatus = "connected" | "disconnected" | "connecting" | "recovering" | "retrying";

export class SerialDevice<T> {
  private connection: SerialPort;

  private readonly vendorId: number;
  private readonly productId: number;
  private readonly bitrate: number;
  private readonly stopBits: 1 | 2;
  private readonly ctsControl: boolean;
  private readonly timeToWait: number;

  // Cancel connection attempts
  private cancelled: boolean = false;
  // Toggle web serial listener
  private keepReading: boolean = true;
  // Buffer to store incoming bytes
  private buffer: number[] = [];
  // Buffer to store formed packets from incoming bytes
  private packetBuffer: T[] = [];
  private readonly packetHandler?: (buffer: BufferRef) => T | null;
  private readonly statusHandler?: (status: SerialDeviceConnectionStatus) => void;
  private readStream: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private portListener: Promise<void> | null = null;
  private _deviceStatus: SerialDeviceConnectionStatus;

  constructor(connection: SerialPort, config: SerialDeviceConfig<T>) {
    this.connection = connection;

    this.vendorId = config.vendorId;
    this.productId = config.productId;
    this.bitrate = withDefault(config.bitrate, 9600);
    this.stopBits = withDefault(config.stopBits, 1);
    this.ctsControl = withDefault(config.ctsControl, false);
    this.timeToWait = withDefault(config.timeToWait, 2000);
    this.packetHandler = config.packetHandler;
    this.statusHandler = config.statusHandler;
    this._deviceStatus = "disconnected";
  }

  bufferRef = () => {
    return {
      get: (n: number) => this.buffer[n],
      len: () => this.buffer.length,
      discard: (n: number) => {
        this.buffer = this.buffer.slice(n);
        return this.buffer;
      },
      bytes: () => {
        return new Uint8Array(this.buffer);
      },
      contains: (n: number) => {
        return this.buffer.indexOf(n) !== -1;
      },
    };
  };

  get deviceStatus() {
    return this._deviceStatus;
  }

  set deviceStatus(status: SerialDeviceConnectionStatus) {
    this._deviceStatus = status;
    if (this.statusHandler) {
      this.statusHandler(this._deviceStatus);
    }
  }

  _receiveSomeBytes = (bytes: Uint8Array) => {
    for (let i = 0; i < bytes.byteLength; i++) {
      this.buffer.push(bytes[i]);
    }

    // we got some bytes, let's see if they make one or more packets
    if (this.packetHandler) {
      let pkt = this.packetHandler(this.bufferRef());
      while (pkt) {
        this.packetBuffer.push(pkt);
        pkt = this.packetHandler(this.bufferRef());
      }
    }
  };

  webSerialListener = async () => {
    while (this.connection.readable && this.keepReading) {
      this.readStream = this.connection.readable.getReader();

      try {
        while (true) {
          const { value, done } = await this.readStream.read();
          if (done) {
            break;
          }
          if (value) {
            this._receiveSomeBytes(value);
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === "NetworkError") {
          console.info("NetworkError, retry connection in 100ms");
          setTimeout(() => this.connect(true), 100);
        }
        Sentry.captureException(error);
      } finally {
        console.info("Closing read stream...");
        if (this.readStream) {
          this.readStream.releaseLock();
        }
      }
    }
  };

  _checkWebSerialPort = async () => {
    // in case the device reconnected, we need to check if we're
    // still connected to the right port
    const existingPermissions = await navigator.serial.getPorts();

    for (let i = 0; i < existingPermissions.length; i++) {
      const { usbProductId, usbVendorId } = existingPermissions[i].getInfo();

      if (this.vendorId === usbVendorId && this.productId === usbProductId) {
        this.connection = existingPermissions[i];
      }
    }
  };

  _openPort = async (connectOpts: ConnectionOpts) => {
    if (this.connection) {
      try {
        console.info(`Connecting using Web Serial API using ${JSON.stringify(connectOpts)}..`);
        await this.connection.open(connectOpts);
        if (this.ctsControl) {
          console.info("Setting flow control...");
          await this.connection.setSignals({
            dataTerminalReady: false,
            requestToSend: true,
          });
        }
        this.portListener = this.webSerialListener();
      } catch (error) {
        console.error("Error during Serial API connect:", error);
        Sentry.captureException(error);
        if (this.readStream && !this.readStream.closed) {
          await this.readStream.cancel();
        }
        if (this.portListener) {
          await this.portListener;
        }
        if (this.connection && this.connection.connected) {
          await this.connection.close();
        }
        await this._checkWebSerialPort();
        throw error;
      }
    }
  };

  connect = async (recoveryMode: boolean = false): Promise<boolean> => {
    this.deviceStatus = "connecting";
    this.cancelled = false;
    const connectOpts = {
      baudRate: this.bitrate,
      autoOpen: false,
      bufferSize: 262144,
      stopBits: this.stopBits,
    };

    /*
    Due to a possible race condition that can occur in node-serialport (see
    https://github.com/node-serialport/node-serialport/issues/1565), we first
    need to make sure we can open the serial port. Can take multiple attempts.
    */
    let success = false;
    while (!success && !this.cancelled) {
      try {
        await this._openPort(connectOpts);
        success = true;
        this.deviceStatus = "connected";
      } catch (error) {
        console.error("Error opening port:", error);
        Sentry.captureException(error);
        this.deviceStatus = recoveryMode ? "recovering" : "retrying";
        await new Promise(resolve => setTimeout(resolve, this.timeToWait));
      }
    }

    if (!success) {
      console.error("Failed to open port after multiple attempts");
      this.deviceStatus = "disconnected";
      return false;
    }

    return true;
  };

  disconnect = async (): Promise<void> => {
    this.deviceStatus = "disconnected";
    this.cancelled = true;
    if (this.connection) {
      this.keepReading = false;
      await this.readStream?.cancel();
      await this.portListener;
      if (this.connection.connected) {
        await this.connection.close();
      }
    }
    this.buffer = [];
    this.packetBuffer = [];
    this.readStream = null;
  };

  discardBytes = (discardCount: number) => {
    this.buffer = this.buffer.slice(discardCount);
  };

  readSerial = async (bytes: number, timeout: number): Promise<number[]> => {
    if (this.buffer.length >= bytes) {
      const packet = this.buffer.slice(0, bytes);
      this.buffer = this.buffer.slice(0 - bytes);
      return packet;
    } else if (timeout === 0) {
      const packet = this.buffer;
      this.buffer = [];
      return packet;
    } else {
      await new Promise(resolve => setTimeout(resolve, timeout));
      return await this.readSerial(bytes, 0);
    }
  };

  writeSerial = async (bytes: Uint8Array): Promise<void> => {
    const buffer = new Uint8Array(bytes);

    if (bytes == null) {
      console.info("just tried to send nothing!");
    } else {
      if (!this.connection) {
        console.warn("No connection details available.");
        throw new Error("No connection details available.");
      } else if (this.connection.writable) {
        const writer = this.connection.writable.getWriter();
        await writer.write(buffer);
        await writer.close();
      }
    }
  };

  hasAvailablePacket = () => {
    return this.packetBuffer.length > 0;
  };

  peekPacket = () => {
    if (this.hasAvailablePacket()) {
      return this.packetBuffer[0];
    } else {
      return null;
    }
  };

  nextPacket = () => {
    if (this.hasAvailablePacket()) {
      return this.packetBuffer.shift();
    } else {
      return undefined;
    }
  };

  flush = () => {
    this.packetBuffer = [];
    this.buffer = [];
  };
}
